import { format } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  errorMessage?: string;
  hasError?: boolean;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  label,
  disabled = false,
  className,
  errorMessage,
  hasError = false,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  const handleSelect = (date: Date | undefined) => {
    onChange?.(date);
    setOpen(false);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return placeholder;
    return format(date, 'dd.MM.yyyy');
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {label && <label className='text-sm font-bold text-[#585858] tracking-[0.56px] leading-5'>{label}</label>}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='ghost'
            disabled={disabled}
            className={cn(
              'h-9 w-full justify-between px-4 py-2 font-bold text-sm tracking-[0.56px]',
              'bg-white border border-zinc-400 rounded-xl',
              'hover:bg-gray-50',
              'focus:border-[#0094aa] focus:ring-2 focus:ring-[#0094aa]/20',
              value && 'bg-[#ebf7f9] border-[#0094aa] text-[#00535f]',
              hasError && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <div className='flex items-center gap-2'>
              <CalendarIcon className='h-5 w-5 text-[#0094aa]' />
              <span className={cn(value ? 'text-[#00535f]' : 'text-[#585858]')}>{formatDate(value)}</span>
            </div>
            <ChevronDown className='h-3 w-3 text-[#006877]' />
          </Button>
        </PopoverTrigger>

        <PopoverContent className='w-auto p-0' align='start'>
          <Calendar mode='single' selected={value} onSelect={handleSelect} disabled={_date => disabled} />
        </PopoverContent>
      </Popover>

      {hasError && errorMessage && (
        <p className='text-xs font-semibold text-red-500 tracking-[0.33px] leading-4'>{errorMessage}</p>
      )}
    </div>
  );
}
